// LiveOrders - the counter screen.
// Every order as it lands, itemized with what the customer paid per line;
// drinks / desserts / chips lines highlighted so the counter sees what must
// go in the bag. Menu list prices live on their own page (MenuPrices).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import socket from '../config/socket';

const api = axios.create({ baseURL: API_URL, withCredentials: true });

const EMOJI = { drinks: '🥤', desserts: '🍰', chips: '🌮' };
const LABEL = { drinks: 'drinks', desserts: 'dessert', chips: 'chips' };
const SOURCE_COLOR = {
  Toast: 'bg-amber-500 text-black', GrubHub: 'bg-orange-500 text-black', ChowNow: 'bg-teal-500 text-black',
  UberEats: 'bg-green-500 text-black', DoorDash: 'bg-red-500 text-white', Caviar: 'bg-purple-500 text-white',
  InStore: 'bg-gray-500 text-white', Kiosk: 'bg-gray-500 text-white', TEST: 'bg-pink-500 text-white',
};
const money = (v) => (v == null ? '' : `$${Number(v).toFixed(2)}`);

function flaggedCategoryFor(order, name) {
  const n = String(name || '').toLowerCase().trim();
  for (const c of Object.keys(order.flagged || {})) {
    if (order.flagged[c].some(f => String(f.name).toLowerCase().trim() === n)) return c;
  }
  return null;
}

function OrderCard({ order, isNew }) {
  const [openLine, setOpenLine] = useState(null); // index of the line whose Toast description is showing
  const cats = order.categories || [];
  const alert = order.alert;
  const border = cats.length
    ? (alert && alert.heardAt ? 'border-yellow-400' : 'border-red-500')
    : 'border-gray-700';
  // Notification-style badge: total quantity of alarm items on this order.
  const alarmCount = cats.reduce((n, c) => n + order.flagged[c].reduce((m, f) => m + (Number(f.quantity) || 1), 0), 0);
  const alarmBreakdown = cats.map(c => `${order.flagged[c].reduce((m, f) => m + (Number(f.quantity) || 1), 0)} ${LABEL[c]}`).join(' · ');
  return (
    <div className={`relative bg-gray-800 rounded-xl border-2 ${border} p-4 mb-4 ${isNew ? 'ring-4 ring-yellow-300' : ''}`}>
      {alarmCount > 0 && (
        <span
          className="absolute -top-3 -left-3 min-w-[32px] h-8 px-2 rounded-full bg-red-600 text-white text-base font-black flex items-center justify-center shadow-lg ring-2 ring-gray-900"
          title={alarmBreakdown}
          aria-label={`${alarmCount} alarm items: ${alarmBreakdown}`}
        >
          🚨{alarmCount}
        </span>
      )}
      <div className={`flex flex-wrap items-center gap-2 mb-2 ${alarmCount > 0 ? 'pl-4' : ''}`}>
        <span className={`px-2 py-0.5 rounded font-bold text-sm ${SOURCE_COLOR[order.source] || 'bg-blue-500 text-white'}`}>{order.source}</span>
        <span className="text-gray-300 font-semibold">{order.orderType}</span>
        {order.customer && <span className="text-white text-lg font-bold">{order.customer}</span>}
        {order.isNewCustomer && <span className="text-xs bg-green-600 text-white px-1.5 py-0.5 rounded">NEW</span>}
        <span className="text-gray-400 text-sm">{order.orderDate} {order.orderTime}</span>
        <span className="text-gray-500 text-xs">#{order.id}</span>
        <span className="ml-auto text-2xl font-black text-green-300">{money(order.total)}</span>
      </div>


      <div className="divide-y divide-gray-700/60">
        {(order.lines || []).map((line, i) => {
          const cat = flaggedCategoryFor(order, line.name);
          // Only priced add-ons on the ticket count as extras. The gap to the
          // in-store menu price is often platform pricing (GrubHub, DoorDash),
          // not toppings, so it is not shown.
          const extras = (line.mods || []).reduce((sum, m) => sum + (Number(m.price) || 0) * (Number(m.quantity) || 1), 0);
          return (
            <div key={i} className="py-2">
              <div className="flex items-start justify-between gap-3">
                <div
                  className={`text-lg leading-tight ${cat ? 'font-extrabold text-yellow-300' : 'text-white font-semibold'} ${line.description ? 'cursor-pointer select-none' : ''}`}
                  onClick={line.description ? () => setOpenLine(openLine === i ? null : i) : undefined}
                  title={line.description ? (openLine === i ? 'Hide the Toast description' : 'Tap to read the Toast description') : undefined}
                >
                  {cat ? `${EMOJI[cat]} ` : ''}{line.quantity}× {line.name}
                  {line.description && <span className="ml-2 text-xs text-blue-300 align-middle">{openLine === i ? '▾' : 'ⓘ'}</span>}
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="text-3xl font-black text-green-300 font-mono leading-none">{money(line.paid)}</div>
                  {line.quantity > 1 && <div className="text-xs text-gray-400">{money(line.unit)} each</div>}
                  {extras >= 0.01 && (
                    <div className="text-xs text-orange-300">includes {money(extras)} in add-ons</div>
                  )}
                </div>
              </div>
              {openLine === i && line.description && (
                <div className="mt-1 mb-1 ml-6 bg-gray-900/70 border border-blue-900 rounded-lg px-3 py-2 text-sm text-blue-100">
                  {line.menuGroup && <div className="text-xs uppercase tracking-wide text-blue-400 mb-1">{line.menuGroup}</div>}
                  {line.description}
                </div>
              )}
              {line.note && <div className="text-yellow-200 text-sm pl-6">✎ {line.note}</div>}
              {(line.mods || []).map((m, j) => {
                const mcat = flaggedCategoryFor(order, m.name);
                return (
                  <div key={j} className={`flex justify-between gap-3 pl-6 text-sm ${mcat ? 'font-extrabold text-yellow-300' : 'text-gray-300'}`}>
                    <span>{mcat ? `${EMOJI[mcat]} ` : '+ '}{m.quantity > 1 ? `${m.quantity}× ` : ''}{m.name}</span>
                    <span className="font-mono text-orange-300 whitespace-nowrap">{m.price > 0 ? money(m.price) : ''}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400">
        {order.subtotal != null && <span>Subtotal {money(order.subtotal)}</span>}
        {order.tax != null && <span>Tax {money(order.tax)}</span>}
        {order.tip != null && order.tip > 0 && <span>Tip {money(order.tip)}</span>}
        {order.paymentType && <span>{order.paymentType}</span>}
      </div>
      {order.notes && <div className="mt-2 text-yellow-200 bg-yellow-900/30 rounded px-2 py-1 text-sm">📝 {order.notes}</div>}
      {alert && (
        <div className={`mt-2 text-xs font-semibold ${alert.heardAt ? 'text-green-400' : 'text-red-400'}`}>
          {alert.heardAt
            ? `🔊 alarm heard on ${alert.heardBy || 'a screen'} at ${new Date(alert.heardAt).toLocaleTimeString()}`
            : alert.seenAt
              ? `👀 alarm shown on ${alert.seenBy || 'a screen'} (no sound)`
              : '⚠️ alarm not heard on any screen'}
          {alert.ownerNotifiedAt ? ' · owner texted' : ''}
        </div>
      )}
    </div>
  );
}

export default function LiveOrders() {
  const [orders, setOrders] = useState([]);
  const [newIds, setNewIds] = useState(new Set());
  const [error, setError] = useState(null);
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => api.get('/alerts/orders', { params: { limit: 40 } })
      .then(r => { if (alive) { setOrders(r.data.orders || []); setError(null); } })
      .catch(e => alive && setError(e.response?.data?.error || e.message));
    load();
    const iv = setInterval(load, 60000);

    const onNew = (order) => {
      setOrders(prev => [order, ...prev.filter(o => o.id !== order.id)].slice(0, 60));
      setNewIds(prev => new Set([...prev, order.id]));
      setTimeout(() => setNewIds(prev => { const n = new Set(prev); n.delete(order.id); return n; }), 60000);
    };
    const onAlert = (a) => {
      if (!a.order || !a.order.id) return;
      setOrders(prev => prev.map(o => o.id === a.order.id ? { ...o, alert: { alertId: a.alertId, createdAt: a.createdAt } } : o));
    };
    const onStatus = (s) => {
      setOrders(prev => prev.map(o => (o.alert && o.alert.alertId === s.alertId)
        ? { ...o, alert: { ...o.alert, heardAt: s.heardAt, heardBy: s.heardBy, seenAt: s.seenAt, seenBy: s.seenBy } }
        : o));
    };
    socket.on('newOrder', onNew);
    socket.on('orderAlert', onAlert);
    socket.on('alertStatus', onStatus);
    return () => {
      alive = false;
      clearInterval(iv);
      socket.off('newOrder', onNew);
      socket.off('orderAlert', onAlert);
      socket.off('alertStatus', onStatus);
    };
  }, []);

  const shown = onlyFlagged ? orders.filter(o => (o.categories || []).length > 0) : orders;

  return (
    <div className="container mx-auto px-4 pb-8 max-w-4xl">
      <div>
          <div className="flex items-center gap-3 mb-3">
            <h2 className="text-xl font-bold">Live Orders</h2>
            <label className="text-sm text-gray-300 flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={onlyFlagged} onChange={e => setOnlyFlagged(e.target.checked)} />
              only orders with 🥤🍰🌮
            </label>
            <span className="ml-auto text-xs text-gray-500">newest first · updates live</span>
          </div>
          {error && <div className="bg-red-900/50 text-red-200 px-3 py-2 rounded mb-3">{error}</div>}
          {shown.length === 0 && !error && <div className="text-gray-400">No orders yet.</div>}
          {shown.map(o => <OrderCard key={o.id} order={o} isNew={newIds.has(o.id)} />)}
      </div>
    </div>
  );
}
